import { createHashRouter } from "react-router-dom";
import Constant from "../../Config/Constant";
import { Suspense, useEffect } from "react";
import Layout from "../../Components/Layout";
import HomeScreen from "../../Screens/HomeScreen";
import { PageSkeleton } from "../../Components/PageLoader";
import PrivacyPolicy from "../../Screens/PrivacyPolicy";
import BookingSuccess from "../../Screens/Busbooking/BookingSuccess";
import VerifyOtp from "../../Components/VerifyOtp";
import MyTickets from "../../Screens/MyTickets";
import MyFaqs from "../../Screens/MyFaqs";
import MyOrderDetails from "../../Screens/MyOrders/MyOrderDetails";
import AdyenCheckoutPage from "../../Screens/Checkout/AdyenCheckoutPage";
import lazyWithRetry from "../lazyWithRetry";
import { RouteErrorFallback } from "../../Components/ErrorBoundary";

// Lazy load the screens with retry logic
const SignInScreen = lazyWithRetry(
  () => import("../../Screens/Auth/SignInScreen"),
);
const SignUpScreen = lazyWithRetry(
  () => import("../../Screens/Auth/SignUpScreen"),
);
const ForgotPassword = lazyWithRetry(
  () => import("../../Screens/Auth/ForgotPassword"),
);
const GiftCards = lazyWithRetry(() => import("../../Screens/GiftCards"));
const ProductDetails = lazyWithRetry(
  () => import("../../Screens/ProductDetails"),
);
const Refill = lazyWithRetry(() => import("../../Screens/Refill"));
const AuthLayout = lazyWithRetry(
  () => import("../../Components/Layout/AuthLayout"),
);
const TermsAndConditions = lazyWithRetry(
  () => import("../../Screens/TermsAndConditions"),
);
const Helps = lazyWithRetry(() => import("../../Screens/Helps"));
const Checkout = lazyWithRetry(() => import("../../Screens/Checkout"));
const BuyNow = lazyWithRetry(() => import("../../Screens/BuyNow"));
const Wishlists = lazyWithRetry(() => import("../../Screens/Wishlists"));
const MyAccount = lazyWithRetry(
  () => import("../../Screens/UsersAccount/MyAccount"),
);
const BusbookingForm = lazyWithRetry(
  () => import("../../Screens/Busbooking/BusbookingForm"),
);
const PaymentSuccess = lazyWithRetry(
  () => import("../../Screens/PaymentSuccess"),
);
const PaymentFailed = lazyWithRetry(
  () => import("../../Screens/PaymentFailed"),
);
const PaymentProcessing = lazyWithRetry(
  () => import("../../Components/Payments/PaymentProcessing"),
);
const BankTransferPending = lazyWithRetry(
  () => import("../../Screens/BankTransferPending"),
);
const MyOrders = lazyWithRetry(() => import("../../Screens/MyOrders"));
const ProductReview = lazyWithRetry(
  () => import("../../Screens/MyOrders/ProductReview"),
);
const FlixBus = lazyWithRetry(
  () => import("../../Screens/Busbooking/BusbookingForm"),
);
const SearchResults = lazyWithRetry(
  () => import("../../Screens/Busbooking/SearchResults"),
);
const PassengerDetails = lazyWithRetry(
  () => import("../../Screens/Busbooking/PassengerDetails"),
);
const ProductRechargeDetails = lazyWithRetry(
  () => import("../../Screens/ProductRechargeDetails"),
);
const MyInvoice = lazyWithRetry(
  () => import("../../Screens/MyOrders/MyInvoice"),
);
const SignInWithOTP = lazyWithRetry(
  () => import("../../Screens/Auth/SignInWithOTP"),
);
const ProductsFaq = lazyWithRetry(() => import("../../Screens/ProductsFaq"));

const NotFound = lazyWithRetry(() => import("../../Screens/NotFound"));

// Wrapper to add a skeleton fallback loader
const LazyLoadWrapper = (Component: React.ComponentType<any>, props?: any) => {
  return (
    <Suspense fallback={<PageSkeleton />}>
      <Component {...(props || {})} />
    </Suspense>
  );
};

const GenerateRoutes = (isAuthenticated: boolean) => {
  useEffect(() => {
    if (isAuthenticated) {
      // console.log(isAuthenticated, 'isAuthenticated @@');
    } else {
      // console.log(isAuthenticated, 'isAuthenticated failed @@');
    }
  }, [isAuthenticated]);

  return [
    {
      path: "",
      element: <Layout />,
      errorElement: <RouteErrorFallback />,
      children: [
        // { path: '/', element: <HomeScreen /> },
        { index: true, element: <HomeScreen /> },
        { path: "gift-cards", element: LazyLoadWrapper(GiftCards) },
        {
          path: "gift-cards/:categoryName",
          element: LazyLoadWrapper(GiftCards),
        },
        {
          path: "gift-cards/:categoryName/:parentCategoryName",
          element: LazyLoadWrapper(GiftCards),
        },
        { path: "refill", element: LazyLoadWrapper(Refill) },
        /*
         * International Top-up: the same listing over its own parent category.
         *
         * It is a top-level category in the catalogue now, a peer of Gift
         * Cards, Gaming and Phone Refill, so it gets a top-level route rather
         * than being reached as a sub-category of Phone Refill -- which would
         * have broken the moment somebody renamed it in Admin.
         */
        {
          path: "international-topup",
          element: LazyLoadWrapper(Refill, {
            parentCategory: Constant.PARENT_CATEGORY_NAME.INTERNATIONAL_TOPUP,
          }),
        },
        {
          path: "refill/:categoryName/:parentCategoryName",
          element: LazyLoadWrapper(Refill),
        },
        {
          path: "gift-cards/product-details/:id/:itemName",
          element: LazyLoadWrapper(ProductDetails),
        },
        {
          path: "product-recharge-details/:id/:itemName",
          element: LazyLoadWrapper(ProductRechargeDetails),
        },
        {
          path: "refill-products-details/:id/:itemName",
          element: LazyLoadWrapper(ProductDetails),
        },
        {
          path: "terms-and-conditions",
          element: LazyLoadWrapper(TermsAndConditions),
        },
        { path: "privacy-policy", element: LazyLoadWrapper(PrivacyPolicy) },
        { path: "helps", element: LazyLoadWrapper(Helps) },
        { path: "wishlist", element: LazyLoadWrapper(Wishlists) },
        { path: "my-account", element: LazyLoadWrapper(MyAccount) },
        { path: "gift-cards/travel", element: LazyLoadWrapper(BusbookingForm) },
        { path: "my-orders", element: LazyLoadWrapper(MyOrders) },
        {
          path: "my-orders/:orderNumber",
          element: LazyLoadWrapper(MyOrderDetails),
        },
        {
          path: "order-invoice/:orderNumber",
          element: LazyLoadWrapper(MyInvoice),
        },
        {
          path: "my-orders/reviews/:orderNumber/:productId",
          element: LazyLoadWrapper(ProductReview),
        },
        { path: "flix-bus", element: LazyLoadWrapper(FlixBus) },
        { path: "search-results", element: LazyLoadWrapper(SearchResults) },
        {
          path: "PassengerDetails",
          element: LazyLoadWrapper(PassengerDetails),
        },
        { path: "busBookingSuccess", element: LazyLoadWrapper(BookingSuccess) },
        { path: "my-tickets", element: LazyLoadWrapper(MyTickets) },
        {
          path: "products-faq/:productId/:productName",
          element: LazyLoadWrapper(ProductsFaq),
        },
        // {path: 'faqs', element: LazyLoadWrapper(Faqs)}
        { path: "my-faqs", element: LazyLoadWrapper(MyFaqs) },
        {
          path: "payment-processing",
          element: LazyLoadWrapper(PaymentProcessing),
        },
        { path: "payment-success", element: LazyLoadWrapper(PaymentSuccess) },
        { path: "payment-cancel", element: LazyLoadWrapper(PaymentFailed) },
        { path: "bank-transfer-pending", element: LazyLoadWrapper(BankTransferPending) },
      ],
    },
    {
      path: "",
      errorElement: <RouteErrorFallback />,
      children: [
        { path: "checkout/buy-now", element: LazyLoadWrapper(BuyNow) },
        { path: "checkout", element: LazyLoadWrapper(Checkout) },
        { path: "adyen-checkout", element: LazyLoadWrapper(AdyenCheckoutPage) },
      ],
    },

    {
      path: "auth",
      element: (
        <Suspense>
          <AuthLayout />
        </Suspense>
      ),
      errorElement: <RouteErrorFallback />,
      children: [
        {
          path: "",
          element: (
            <Suspense>
              <SignInScreen />
            </Suspense>
          ),
        },
        { path: "signin", element: LazyLoadWrapper(SignInScreen) },
        { path: "create-account", element: LazyLoadWrapper(SignUpScreen) },
        { path: "forgot-password", element: LazyLoadWrapper(ForgotPassword) },
        { path: "reset-password", element: LazyLoadWrapper(ForgotPassword) },
        {
          path: "signin-with-otp",
          element: (
            <Suspense>
              <SignInWithOTP />
            </Suspense>
          ),
        },
        {
          path: "verify-email-otp",
          element: (
            <Suspense>
              <VerifyOtp />
            </Suspense>
          ),
        },
      ],
    },
    {
      path: "*",
      element: LazyLoadWrapper(NotFound),
    },
  ];
};

// Create the router object dynamically
export const createAppRouter = (isAuthenticated: boolean) => {
  return createHashRouter(GenerateRoutes(isAuthenticated), {
    basename: process.env.REACT_APP_BASE_NAME,
  });
};
